import { LotSplitSummary, SaleLot, SaleLotData } from "./LotData";
import { AgTechApiEntityResponse } from "@agtech/core/data/actions/AgTechHttpRequests";
import { AgTechIndex, Index } from "@agtech/core/utilities/AgTechUtilities";
import { LotDataGetLotCategoriesQuery } from "./categories/LotCategoryQueries";
import { AgTechQuery, AgTechQueryRun, useAgTechQuery, useAgTechQueryCombination, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { useCurrentSale } from "../sales/SaleDataStore";
import { useBidderDataGetSaleBidderDataQuery } from "../bidders/BidderDataQueries";
import AgTechQueryLoadingSurface, { AgTechQueryLoadingSurfaceSettings } from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import { AgTechInvalidateQueryDataProps } from "@agtech/core/data/actions/AgTechDataActions";

/// DTOs

type LotSplitDTO = {
    bidderId?: number,
    contactId: number,
    percentage: number
}

/// Queries

export const LotDataGetLotsQuery: AgTechQuery<{ saleId: number }, AgTechIndex<SaleLot>> = {
    queryKey: () => ['Lots'],
    query: (props) => {
        return props.http.getEntity<SaleLot[], AgTechIndex<SaleLot>>({
            request: 'Lot/' + props.inputData.saleId,
            convert: lots => Index(lots, lot => lot.id)
        });
    },
    content: {
        loadingText: 'Loading lots...',
        failureText: 'Failed to load lots.'
    }
}

export const LotDataGetLotSplitSummaryQuery: AgTechQuery<SaleLot, LotSplitSummary> = {
    queryKey: (props) => ['LotSplitSummary', props.id],
    query: (props) => {
        return props.http.get<AgTechApiEntityResponse<LotSplitDTO[]>, LotSplitSummary>({
            request: 'Lot/Splits/' + props.inputData.id,
            convert: (result) => ({
                lot: props.inputData,
                splits: result.data.map(result => ({ 
                    contactId: result.contactId,
                    bidderId: result.bidderId,
                    percentage: result.percentage
                }))
            })
        });
    },
    content: {
        loadingText: 'Loading lot splits...',
        failureText: 'Failed to load lot splits.'
    }
}

///
/// Composite Queries
///

export const useLotDataGetSaleLotsQuery = () => {
    const currentSale = useCurrentSale();

    return useAgTechQuery({
        query: LotDataGetLotsQuery,
        queryContext: { saleId: currentSale.id }
    });
}

export const useLotDataGetSaleLotDataQuery = (): AgTechQueryRun<SaleLotData> => {
    const currentSale = useCurrentSale();

    const lotDataQuery = useAgTechQueryCombination({
        firstQuery: {
            query: LotDataGetLotsQuery,
            queryContext: { saleId: currentSale.id }
        },
        secondQuery: {
            query: LotDataGetLotCategoriesQuery
        },
        projection: (lots, categories) => {
            return {
                lots: lots,
                lotCategories: categories
            }
        }
    });

    const bidderDataQuery = useBidderDataGetSaleBidderDataQuery();

    return useAgTechQueryResultCombination({
        firstQuery: lotDataQuery,
        secondQuery: bidderDataQuery,
        projection: (lotData, bidderData) => {
            return {
                lots: lotData.lots,
                lotCategories: lotData.lotCategories,
                bidders: bidderData.bidders,
                contacts: bidderData.contacts
            }
        }
    });
}

/// Providers

export const SaleLotsDataProvider = (props: AgTechQueryLoadingSurfaceSettings & {
    children: (lots: SaleLotData) => JSX.Element
}) => {
    const lotDataQuery = useLotDataGetSaleLotDataQuery();

    return (
        <AgTechQueryLoadingSurface query={lotDataQuery} {...props}>
            {props.children}
        </AgTechQueryLoadingSurface>
    );
}

export const LotProvider = (props: {
    lotId: number,
    children: (lot: SaleLot) => JSX.Element
}) => {
    return (
        <SaleLotsDataProvider>
            {(lotData) => {
                return props.children(lotData.lots[props.lotId]);
            }}
        </SaleLotsDataProvider>
    )
};